/**
 * OnTheHouse Data Parser
 * 
 * Parses HTML data from OnTheHouse API responses to extract property features.
 * Used as fallback when Domain data shows N/A.
 */

/**
 * Extract property features from OnTheHouse HTML data
 * @param {Object} onthehouseData - OnTheHouse data object
 * @returns {Object} Extracted features { beds, baths, parking, landSize, propertyType }
 */
export const parseOnTheHouseFeatures = (onthehouseData) => {
  const features = {
    beds: null,
    baths: null,
    parking: null,
    landSize: null,
    floorArea: null,
    propertyType: null,
    propertyValue: null,
    weeklyRent: null,
    rentalYield: null,
    builtYear: null
  }

  if (!onthehouseData) return features

  try {
    // OnTheHouse data can be in array format with fetched_at
    let htmlContent = ''
    let propertyFeaturesString = ''

    if (Array.isArray(onthehouseData) && onthehouseData.length > 0) {
      // Get the latest entry
      const latestEntry = onthehouseData[onthehouseData.length - 1]
      const data = latestEntry.data || latestEntry

      // PRIORITY 1: Check property_features field (pipe-separated string)
      if (data.property_features && Array.isArray(data.property_features) && data.property_features.length > 0) {
        propertyFeaturesString = data.property_features[0]
        console.log('📊 Found property_features string:', propertyFeaturesString)
      }

      // Check sales_nearby array
      if (data.sales_nearby && Array.isArray(data.sales_nearby)) {
        htmlContent = data.sales_nearby.join(' ')
      }

      // Also check key_features
      if (data.key_features && Array.isArray(data.key_features)) {
        htmlContent += ' ' + data.key_features.join(' ')
      }

      // Also check estimated_value
      if (data.estimated_value && Array.isArray(data.estimated_value)) {
        htmlContent += ' ' + data.estimated_value.join(' ')
      }
    } else if (typeof onthehouseData === 'object') {
      // Direct object format

      // PRIORITY 1: Check property_features field (pipe-separated string)
      if (onthehouseData.property_features && Array.isArray(onthehouseData.property_features) && onthehouseData.property_features.length > 0) {
        propertyFeaturesString = onthehouseData.property_features[0]
        console.log('📊 Found property_features string:', propertyFeaturesString)
      }

      if (onthehouseData.sales_nearby && Array.isArray(onthehouseData.sales_nearby)) {
        htmlContent = onthehouseData.sales_nearby.join(' ')
      }
      if (onthehouseData.key_features && Array.isArray(onthehouseData.key_features)) {
        htmlContent += ' ' + onthehouseData.key_features.join(' ')
      }
      if (onthehouseData.estimated_value && Array.isArray(onthehouseData.estimated_value)) {
        htmlContent += ' ' + onthehouseData.estimated_value.join(' ')
      }
    }

    // Parse property_features string first (highest priority)
    if (propertyFeaturesString) {
      console.log('📊 Parsing property_features string...')

      // Helper to clean HTML tags
      const cleanHtml = (str) => {
        if (!str) return ''
        return str
          .replace(/<[^>]*>/g, '') // Remove all HTML tags
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
          .replace(/\s+/g, ' ')
          .trim()
      }

      // Split by pipe and parse each feature
      const parts = propertyFeaturesString.split('|').map(part => part.trim())

      parts.forEach(part => {
        const colonIndex = part.indexOf(':')
        if (colonIndex > -1) {
          const key = part.slice(0, colonIndex).trim().toLowerCase()
          const rawValue = part.slice(colonIndex + 1).trim()
          const value = cleanHtml(rawValue)

          console.log(`  - ${key}: ${value}`)

          if (key.includes('bedroom') || key.includes('bed')) {
            const match = value.match(/(\d+)/)
            if (match) features.beds = parseInt(match[1])
          } else if (key.includes('bathroom') || key.includes('bath')) {
            const match = value.match(/(\d+)/)
            if (match) features.baths = parseInt(match[1])
          } else if (key.includes('car') || key.includes('parking')) {
            const match = value.match(/(\d+)/)
            if (match) features.parking = parseInt(match[1])
          } else if (key.includes('floor area') || key.includes('internal area') || key.includes('building area')) {
            features.floorArea = value
          } else if (key.includes('land area') || key.includes('land size') || key.includes('lot size')) {
            features.landSize = value
          } else if (key.includes('type') || key.includes('property type')) {
            features.propertyType = value
          } else if (key.includes('built') || key.includes('year')) {
            const match = value.match(/(\d{4})/)
            if (match) features.builtYear = match[1]
          }
        }
      })

      console.log('📊 Parsed features from property_features:', features)
    }

    if (!htmlContent && !propertyFeaturesString) return features

    // Create a temporary DOM element to parse HTML
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    
    // Extract bedrooms
    const bedroomElements = doc.querySelectorAll('[title="Bedrooms"]')
    if (bedroomElements.length > 0) {
      const bedroomText = bedroomElements[0].textContent
      const bedroomMatch = bedroomText.match(/(\d+)/)
      if (bedroomMatch) {
        features.beds = parseInt(bedroomMatch[1])
      }
    }
    
    // Fallback: search in text content
    if (!features.beds) {
      const bedroomMatch = htmlContent.match(/Bedrooms:\s*<\/span>(\d+)/i) || 
                          htmlContent.match(/(\d+)\s*bed/i)
      if (bedroomMatch) {
        features.beds = parseInt(bedroomMatch[1])
      }
    }

    // Extract bathrooms
    const bathroomElements = doc.querySelectorAll('[title="Bathrooms"]')
    if (bathroomElements.length > 0) {
      const bathroomText = bathroomElements[0].textContent
      const bathroomMatch = bathroomText.match(/(\d+)/)
      if (bathroomMatch) {
        features.baths = parseInt(bathroomMatch[1])
      }
    }
    
    // Fallback: search in text content
    if (!features.baths) {
      const bathroomMatch = htmlContent.match(/Bathrooms:\s*<\/span>(\d+)/i) || 
                           htmlContent.match(/(\d+)\s*bath/i)
      if (bathroomMatch) {
        features.baths = parseInt(bathroomMatch[1])
      }
    }

    // Extract parking/car spaces
    const parkingElements = doc.querySelectorAll('[title="Car spaces"]')
    if (parkingElements.length > 0) {
      const parkingText = parkingElements[0].textContent
      const parkingMatch = parkingText.match(/(\d+)/)
      if (parkingMatch) {
        features.parking = parseInt(parkingMatch[1])
      }
    }
    
    // Fallback: search in text content
    if (!features.parking) {
      const parkingMatch = htmlContent.match(/Car spaces:\s*<\/span>(\d+)/i) || 
                          htmlContent.match(/(\d+)\s*car/i)
      if (parkingMatch) {
        features.parking = parseInt(parkingMatch[1])
      }
    }

    // Extract property type
    const typeElements = doc.querySelectorAll('[data="seoData"]')
    for (const el of typeElements) {
      const text = el.textContent
      if (text.includes('Type:')) {
        const typeMatch = text.match(/Type:\s*<\/span>(\w+)/i) || 
                         text.match(/Type:\s*(\w+)/i)
        if (typeMatch) {
          features.propertyType = typeMatch[1].trim()
        }
      }
    }
    
    // Fallback: search in text content
    if (!features.propertyType) {
      const typeMatch = htmlContent.match(/Type:\s*<\/span>(\w+)/i) || 
                       htmlContent.match(/Type:\s*(\w+)/i)
      if (typeMatch) {
        features.propertyType = typeMatch[1].trim()
      }
    }

    // Extract land size from HTML if not already found
    if (!features.landSize && htmlContent) {
      const landSizeMatch = htmlContent.match(/(\d+(?:,\d+)?)\s*m[²2]/i)
      if (landSizeMatch) {
        features.landSize = landSizeMatch[1].replace(',', '') + 'm²'
      }
    }

    // Extract property value / price from HTML if not already found
    if (!features.propertyValue && htmlContent) {
      // Look for patterns like: "$1,080,000", "Price: $850,000", "Sold for $1.2M"
      const priceMatch = htmlContent.match(/\$([0-9,]+(?:\.[0-9]+)?[KkMm]?)/i)
      if (priceMatch) {
        let price = priceMatch[1].replace(/,/g, '')
        // Handle K/M suffixes
        if (price.toLowerCase().endsWith('k')) {
          price = parseFloat(price) * 1000
        } else if (price.toLowerCase().endsWith('m')) {
          price = parseFloat(price) * 1000000
        }
        features.propertyValue = `$${parseInt(price).toLocaleString()}`
      }
    }

    // Extract weekly rent from HTML if not already found
    if (!features.weeklyRent && htmlContent) {
      // Look for patterns like: "Current Rental Appraisal: $660 P/W", "$660 per week", "$660/week"
      const rentMatch = htmlContent.match(/\$([0-9,]+)\s*(?:P\/W|per week|\/week|pw)/i)
      if (rentMatch) {
        const rent = rentMatch[1].replace(/,/g, '')
        features.weeklyRent = `$${parseInt(rent).toLocaleString()}`
      }
    }

    // Calculate rental yield if we have both property value and weekly rent
    if (features.propertyValue && features.weeklyRent) {
      const value = parseFloat(features.propertyValue.replace(/[$,]/g, ''))
      const rent = parseFloat(features.weeklyRent.replace(/[$,]/g, ''))
      if (value && rent) {
        const annualRent = rent * 52
        const yieldPercent = (annualRent / value) * 100
        features.rentalYield = `${yieldPercent.toFixed(2)}%`
      }
    }

    console.log('📊 Final parsed OnTheHouse features:', features)
    
  } catch (error) {
    console.error('❌ Error parsing OnTheHouse data:', error)
  }

  return features
}

/**
 * Get property feature with fallback from Domain to OnTheHouse
 * @param {*} domainValue - Value from Domain data
 * @param {Object} onthehouseData - OnTheHouse data object
 * @param {string} featureKey - Feature key (beds, baths, parking, landSize, propertyType)
 * @returns {*} Feature value or 'N/A'
 */
export const getFeatureWithFallback = (domainValue, onthehouseData, featureKey) => {
  // Check if domain value is valid
  const isValidDomainValue = domainValue && 
                             domainValue !== 'N/A' && 
                             domainValue !== '' && 
                             domainValue !== null && 
                             domainValue !== undefined

  if (isValidDomainValue) {
    return domainValue
  }

  // Fallback to OnTheHouse
  console.log(`⚠️ Domain ${featureKey} is N/A, falling back to OnTheHouse`)
  const onthehouseFeatures = parseOnTheHouseFeatures(onthehouseData)
  const onthehouseValue = onthehouseFeatures[featureKey]

  if (onthehouseValue !== null && onthehouseValue !== undefined) {
    console.log(`✅ Found ${featureKey} in OnTheHouse: ${onthehouseValue}`)
    return onthehouseValue
  }

  return 'N/A'
}

/**
 * Extract key features text from OnTheHouse data
 * @param {Object} onthehouseData - OnTheHouse data object
 * @returns {string} Cleaned key features text
 */
export const extractKeyFeatures = (onthehouseData) => {
  if (!onthehouseData) return ''

  try {
    let htmlContent = ''
    
    if (Array.isArray(onthehouseData) && onthehouseData.length > 0) {
      const latestEntry = onthehouseData[onthehouseData.length - 1]
      const data = latestEntry.data || latestEntry
      
      if (data.key_features && Array.isArray(data.key_features)) {
        htmlContent = data.key_features.join(' ')
      }
    } else if (typeof onthehouseData === 'object') {
      if (onthehouseData.key_features && Array.isArray(onthehouseData.key_features)) {
        htmlContent = onthehouseData.key_features.join(' ')
      }
    }

    if (!htmlContent) return ''

    // Parse HTML and extract text
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    const text = doc.body.textContent || ''
    
    // Clean up the text
    return text
      .replace(/\s+/g, ' ')
      .replace(/<[^>]*>/g, '')
      .trim()
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join('\n')
      
  } catch (error) {
    console.error('❌ Error extracting key features:', error)
    return ''
  }
}

/**
 * Extract sales nearby data from OnTheHouse
 * @param {Object} onthehouseData - OnTheHouse data object
 * @returns {Array} Array of nearby sales with extracted data
 */
export const extractSalesNearby = (onthehouseData) => {
  const sales = []

  if (!onthehouseData) return sales

  try {
    let htmlContent = ''

    if (Array.isArray(onthehouseData) && onthehouseData.length > 0) {
      const latestEntry = onthehouseData[onthehouseData.length - 1]
      const data = latestEntry.data || latestEntry

      if (data.sales_nearby && Array.isArray(data.sales_nearby)) {
        htmlContent = data.sales_nearby.join(' ')
      }
    } else if (typeof onthehouseData === 'object') {
      if (onthehouseData.sales_nearby && Array.isArray(onthehouseData.sales_nearby)) {
        htmlContent = onthehouseData.sales_nearby.join(' ')
      }
    }

    if (!htmlContent) return sales

    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')

    // Find all property cards
    const propertyCards = doc.querySelectorAll('.PropertyCardSearch__propertyCard--FqRCV')

    propertyCards.forEach(card => {
      const sale = {}

      // Extract address
      const addressElements = card.querySelectorAll('.PropertyCardSearch__propertyCardAddressText--Cav7-')
      if (addressElements.length > 0) {
        sale.address = Array.from(addressElements).map(el => el.textContent.trim()).join(' ')
      }

      // Extract price
      const priceElement = card.querySelector('.PropertyCardSearch__propertyCardPrice--EhKc\\+')
      if (priceElement) {
        const priceText = priceElement.textContent
        const priceMatch = priceText.match(/\$([0-9,]+)/)
        if (priceMatch) {
          sale.price = priceMatch[1].replace(/,/g, '')
        }
      }

      // Extract features
      const bedroomEl = card.querySelector('[title="Bedrooms"]')
      if (bedroomEl) {
        const match = bedroomEl.textContent.match(/(\d+)/)
        if (match) sale.beds = parseInt(match[1])
      }

      const bathroomEl = card.querySelector('[title="Bathrooms"]')
      if (bathroomEl) {
        const match = bathroomEl.textContent.match(/(\d+)/)
        if (match) sale.baths = parseInt(match[1])
      }

      const parkingEl = card.querySelector('[title="Car spaces"]')
      if (parkingEl) {
        const match = parkingEl.textContent.match(/(\d+)/)
        if (match) sale.parking = parseInt(match[1])
      }

      if (sale.address) {
        sales.push(sale)
      }
    })

  } catch (error) {
    console.error('❌ Error extracting sales nearby:', error)
  }

  return sales
}

/**
 * Parse the plain-text "Timeline events: Event date=..., Event description=...; ..." format
 * that is stored in the OnTheHouse DB property_history array.
 *
 * Example input:
 *   "Timeline events: Event date=30 Jan 2026, Event description=Listed for Sale $720,000 - $ 792,000; Event date=15 Feb 2005, Event description=Transferred"
 *
 * @param {string} str - The raw timeline string from the DB
 * @returns {Array} Array of { date, transaction_type, rawLabel, price, description } records
 */
export const parseTimelineEventsString = (str) => {
  if (!str || typeof str !== 'string') return []

  const records = []

  // Strip the leading "Timeline events: " prefix if present
  const content = str.replace(/^Timeline events:\s*/i, '').trim()

  // Split on semicolons to get individual events
  const entries = content.split(';').map(e => e.trim()).filter(Boolean)

  entries.forEach(entry => {
    // Match: "Event date=<date>, Event description=<description>"
    const dateMatch = entry.match(/Event date=([^,]+)/i)
    const descMatch = entry.match(/Event description=(.+)/i)

    if (!dateMatch) return

    const date = dateMatch[1].trim()
    const description = descMatch ? descMatch[1].trim() : ''

    // Determine transaction type from description
    // rawLabel is always a SHORT clean label for the badge — never the full description with price
    let transactionType = 'Record'
    let price = null
    let rawLabel = 'Event'

    if (/^Transferred\b/i.test(description)) {
      transactionType = 'TRANSFERRED'
      rawLabel = 'Transferred'
    } else if (/Sold\s+for/i.test(description)) {
      transactionType = 'SOLD'
      const priceMatch = description.match(/\$(\d[\d,]*)/)
      if (priceMatch) price = `$${priceMatch[1]}`
      rawLabel = 'Sold'
    } else if (/Listed\s+for\s+Rent/i.test(description)) {
      transactionType = 'LEASED'
      const priceMatch = description.match(/\$(\d[\d,]*)/)
      if (priceMatch) price = `$${priceMatch[1]}/week`
      rawLabel = 'Listed for rent'
    } else if (/Listed\s+for\s+Sale/i.test(description)) {
      transactionType = 'LISTED'
      // Extract price range like "$720,000 - $ 792,000"
      const rangeMatch = description.match(/\$(\d[\d,]*)\s*-\s*\$?\s*(\d[\d,]*)/)
      if (rangeMatch) {
        price = `$${rangeMatch[1]}-$${rangeMatch[2]}`
      } else {
        const priceMatch = description.match(/\$(\d[\d,]*)/)
        if (priceMatch) price = `$${priceMatch[1]}`
      }
      rawLabel = 'Listed for sale'
    }

    records.push({
      date,
      transaction_type: transactionType,
      rawLabel,
      price,
      description,
      agency: null,
      price_unit: 'total'
    })
  })

  return records
}

/**
 * Extract property history from OnTheHouse data
 * @param {Object} onthehouseData - OnTheHouse data object
 * @returns {Array} Array of property history records
 */
export const extractPropertyHistory = (onthehouseData) => {
  const history = []

  if (!onthehouseData) return history

  try {
    let htmlContent = ''

    if (Array.isArray(onthehouseData) && onthehouseData.length > 0) {
      const latestEntry = onthehouseData[onthehouseData.length - 1]
      const data = latestEntry.data || latestEntry

      // Check for property_history field
      if (data.property_history && Array.isArray(data.property_history)) {
        htmlContent = data.property_history.join(' ')
      }

      // Also check key_features for historical sale information
      if (!htmlContent && data.key_features && Array.isArray(data.key_features)) {
        htmlContent = data.key_features.join(' ')
      }
    } else if (typeof onthehouseData === 'object') {
      if (onthehouseData.property_history && Array.isArray(onthehouseData.property_history)) {
        htmlContent = onthehouseData.property_history.join(' ')
      }
      if (!htmlContent && onthehouseData.key_features && Array.isArray(onthehouseData.key_features)) {
        htmlContent = onthehouseData.key_features.join(' ')
      }
    }

    if (!htmlContent) return history

    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')

    // Look for event items in the PropertyHistory component
    // Format: <li class="...PropertyHistory__eventItem...">
    const eventItems = doc.querySelectorAll('li.PropertyHistory__eventItem--tUpLw, li[class*="PropertyHistory__eventItem"]')

    if (eventItems.length > 0) {
      console.log(`📜 Found ${eventItems.length} property history events in OnTheHouse HTML`)

      eventItems.forEach(item => {
        const dateElement = item.querySelector('.text-secondary, [class*="text-secondary"]')
        const descriptionElement = item.querySelector('.bold600.mdText, [class*="bold600"]')

        if (dateElement && descriptionElement) {
          const date = dateElement.textContent.trim()
          const description = descriptionElement.textContent.trim()

          // Skip "Sign in to see more" items
          if (description.toLowerCase().includes('sign in')) {
            return
          }

          // Determine transaction type and extract price
          // Preserve the original description text so the UI can show the exact label from the source
          let transactionType = 'Record'
          let price = 'Price not disclosed'

          // Check for "Sold for $XXX"
          const soldMatch = description.match(/Sold\s+for\s+\$([0-9,]+)/i)
          if (soldMatch) {
            transactionType = 'SOLD'
            price = `$${soldMatch[1]}`
          }

          // Check for "Transferred" (no price – title transfer)
          const transferredMatch = description.match(/^Transferred\b/i)
          if (transferredMatch) {
            transactionType = 'TRANSFERRED'
          }

          // Check for "Listed for Sale ... $XXX" or "Listed for Sale Offers Over $XXX"
          const listedSaleMatch = description.match(/Listed\s+for\s+Sale.*?\$([0-9,]+)/i)
          if (listedSaleMatch) {
            transactionType = 'LISTED'
            price = `$${listedSaleMatch[1]}`
          }

          // Check for "Listed for Rent $XXX/w"
          const listedRentMatch = description.match(/Listed\s+for\s+Rent.*?\$([0-9,]+)\/w/i)
          if (listedRentMatch) {
            transactionType = 'LEASED'
            price = `$${listedRentMatch[1]}/week`
          }

          history.push({
            transaction_type: transactionType,
            // rawLabel lets the UI display the exact original event text (e.g. "Transferred", "Sold for $...", etc.)
            rawLabel: description.split('\n')[0].trim() || description.trim(),
            price: price,
            date: date,
            agency: 'Agency unknown',
            price_unit: 'total',
            description: description
          })
        }
      })
    } else {
      // Fallback to regex patterns if DOM parsing doesn't work
      console.log('📜 No event items found, using regex fallback')

      // Pattern: "Sold for $XXX" followed by date
      const soldMatches = htmlContent.matchAll(/Sold\s+for\s+\$([0-9,]+).*?([0-9]{1,2}\s+[A-Za-z]+\s+[0-9]{4})/gi)

      for (const match of soldMatches) {
        const price = match[1] ? `$${match[1]}` : 'Price not disclosed'
        const date = match[2] || 'Date unknown'

        history.push({
          transaction_type: 'SOLD',
          rawLabel: `Sold for $${match[1]}`,
          price: price,
          date: date,
          agency: 'Agency unknown',
          price_unit: 'total'
        })
      }

      // Pattern: "Transferred" followed by date
      const transferredMatches = htmlContent.matchAll(/Transferred.*?([0-9]{1,2}\s+[A-Za-z]+\s+[0-9]{4})/gi)
      for (const match of transferredMatches) {
        const date = match[1] || 'Date unknown'
        history.push({
          transaction_type: 'TRANSFERRED',
          rawLabel: 'Transferred',
          price: 'Price not disclosed',
          date: date,
          agency: 'Agency unknown',
          price_unit: 'total'
        })
      }

      // Pattern: "Listed for Sale ... $XXX" followed by date
      const listedMatches = htmlContent.matchAll(/Listed\s+for\s+Sale.*?\$([0-9,]+).*?([0-9]{1,2}\s+[A-Za-z]+\s+[0-9]{4})/gi)

      for (const match of listedMatches) {
        const price = match[1] ? `$${match[1]}` : 'Price not disclosed'
        const date = match[2] || 'Date unknown'

        history.push({
          transaction_type: 'LISTED',
          price: price,
          date: date,
          agency: 'Agency unknown',
          price_unit: 'total'
        })
      }

      // Pattern: "Listed for Rent $XXX/w" followed by date
      const rentMatches = htmlContent.matchAll(/Listed\s+for\s+Rent.*?\$([0-9,]+)\/w.*?([0-9]{1,2}\s+[A-Za-z]+\s+[0-9]{4})/gi)

      for (const match of rentMatches) {
        const price = match[1] ? `$${match[1]}/week` : 'Price not disclosed'
        const date = match[2] || 'Date unknown'

        history.push({
          transaction_type: 'LEASED',
          price: price,
          date: date,
          agency: 'Agency unknown',
          price_unit: 'total'
        })
      }
    }

    console.log('📜 Extracted OnTheHouse property history:', history)

  } catch (error) {
    console.error('❌ Error extracting property history:', error)
  }

  return history
}